import { useState, useEffect, useCallback } from 'react';
import { useCampus } from '../lib/useCampus';
import { api, apiUrl, ApiError, asList } from '../lib/api';
import { reportCardsApi, type ReportCard } from '../lib/reportCardsApi';

interface ClassLite { id: string; label: string; }
interface ExamLite { id: string; name: string; subjectId: string; maxMarks: number; date: string; }

function currentAcademicYear() {
  const n = new Date();
  return n.getMonth() >= 5 ? `${n.getFullYear()}-${n.getFullYear() + 1}` : `${n.getFullYear() - 1}-${n.getFullYear()}`;
}

export function ReportCardsPage() {
  const [instId, setInstId] = useState<string | null>(null);
  const [classes, setClasses] = useState<ClassLite[]>([]);
  const [classId, setClassId] = useState('');
  const [term, setTerm] = useState('Term 1');
  const [exams, setExams] = useState<ExamLite[]>([]);
  const [chosenExams, setChosenExams] = useState<string[]>([]);
  const [cards, setCards] = useState<ReportCard[]>([]);
  const [detail, setDetail] = useState<ReportCard | null>(null);

  /* ⚠ Was: fetch one student and read their campus — "whichever campus the first student row
     happens to belong to". Arbitrary on a multi-campus trust. See lib/useCampus.ts. */
  const { institutionId: campusId } = useCampus();
  useEffect(() => { setInstId(campusId); }, [campusId]);
  useEffect(() => {
    if (!instId) return;
    api.get<ClassLite[]>(`/api/classes?institutionId=${instId}`).then((r) => setClasses(asList(r))).catch(() => setClasses([]));
  }, [instId]);
  useEffect(() => {
    if (!classId) { setExams([]); return; }
    api.get<ExamLite[]>(`/api/exams?academicClassId=${classId}`).then((r) => setExams(asList(r))).catch(() => setExams([]));
  }, [classId]);

  const loadCards = useCallback(() => {
    if (!classId || !term) return;
    reportCardsApi.forClassTerm(classId, term).then((r) => setCards(asList(r))).catch(() => setCards([]));
  }, [classId, term]);
  useEffect(loadCards, [loadCards]);

  const toggleExam = (id: string) => setChosenExams((c) => c.includes(id) ? c.filter((x) => x !== id) : [...c, id]);

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div className="eyebrow" style={{ marginBottom: 6 }}>Academics</div>
        <h1 className="h-display" style={{ fontSize: 28 }}>Report cards</h1>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginTop: 4 }}>
          Build cards from entered exam marks, review, then finalize. A card is only visible to parents once finalized —
          and it can't be finalized while any subject's mark is missing.
        </p>
      </div>

      <div className="card" style={{ padding: 18, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <label className="label">Class</label>
            <select className="input" value={classId} onChange={(e) => setClassId(e.target.value)}>
              <option value="">Select class…</option>
              {classes.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Term</label>
            <input className="input" value={term} onChange={(e) => setTerm(e.target.value)} style={{ width: 160 }} />
          </div>
        </div>

        {classId && (
          <>
            <div className="label">Exams in this term ({chosenExams.length} chosen)</div>
            {exams.length === 0
              ? <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>No exams for this class yet. Create exams and enter marks first.</div>
              : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 }}>
                  {exams.map((e) => (
                    <button key={e.id} className={chosenExams.includes(e.id) ? 'btn btn-accent' : 'btn'} style={{ padding: '4px 10px' }} onClick={() => toggleExam(e.id)}>
                      {e.name} <span style={{ opacity: 0.7, fontSize: 11 }}>/{e.maxMarks}</span>
                    </button>
                  ))}
                </div>}
          </>
        )}
      </div>

      {classId && chosenExams.length > 0 &&
        <BuildRow classId={classId} instId={instId} term={term} examIds={chosenExams} onBuilt={loadCards} />}

      <h2 className="h-display" style={{ fontSize: 18, marginBottom: 10 }}>Cards for {term}</h2>
      {cards.length === 0
        ? <div className="card" style={{ padding: 20, color: 'var(--ink-3)' }}>No cards built yet.</div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {cards.map((c) => (
              <div key={c.id} className="card" style={{ padding: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>{c.studentName}</span>
                  {c.status === 'Finalized'
                    ? <span className="pill" style={{ marginLeft: 8, fontSize: 11, background: '#e7f6ec', color: '#1a7f37' }}>Finalized · {c.percentage}%{c.overallGrade ? ` · ${c.overallGrade}` : ''}</span>
                    : <span className="pill" style={{ marginLeft: 8, fontSize: 11 }}>Draft{c.unresolved.length ? ` · ${c.unresolved.length} missing` : ' · ready'}</span>}
                </div>
                <button className="btn" onClick={() => setDetail(c)}>Open</button>
              </div>
            ))}
          </div>}

      {detail && <CardDetail card={detail} onClose={() => { setDetail(null); loadCards(); }} />}
    </div>
  );
}

function BuildRow({ classId, instId, term, examIds, onBuilt }: { classId: string; instId: string | null; term: string; examIds: string[]; onBuilt: () => void }) {
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Array<{ id: string; fullName: string; admissionNumber: string }>>([]);
  const [msg, setMsg] = useState<string | null>(null);

  const doSearch = async () => {
    if (search.trim().length < 2) return;
    try { setResults(asList(await api.get(`/api/students?search=${encodeURIComponent(search.trim())}&take=8`))); } catch { setResults([]); }
  };

  const build = async (studentId: string, name: string) => {
    setMsg(null);
    try {
      await reportCardsApi.build({ studentId, academicClassId: classId, academicYear: currentAcademicYear(), term, examIds, includeAttendance: true });
      setMsg(`Draft built for ${name}.`); setResults([]); setSearch(''); onBuilt();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not build.'); }
  };

  return (
    <div className="card" style={{ padding: 16, marginBottom: 16, background: 'var(--surface-2,#f6f6f4)' }}>
      <div style={{ fontWeight: 600, marginBottom: 8 }}>Build a card for a student</div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
        <input className="input" placeholder="Find student" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doSearch()} />
        <button className="btn" onClick={doSearch} disabled={!instId}>Search</button>
      </div>
      {results.map((s) => (
        <button key={s.id} className="btn" style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 4 }} onClick={() => build(s.id, s.fullName)}>
          {s.fullName} · {s.admissionNumber}
        </button>
      ))}
      {msg && <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6 }}>{msg}</div>}
    </div>
  );
}

function CardDetail({ card, onClose }: { card: ReportCard; onClose: () => void }) {
  const [c, setC] = useState(card);
  const [remarks, setRemarks] = useState(card.remarks ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Board layout for printing. Defaults to CBSE; a PU or engineering institution picks its own.
  // '' = let the school's recorded board decide, which is the normal case. The dropdown is an
  // override for a one-off, not something staff should have to get right 800 times.
  const [format, setFormat] = useState(() => localStorage.getItem('eduvora.rcFormat') ?? '');
  useEffect(() => { localStorage.setItem('eduvora.rcFormat', format); }, [format]);
  const finalized = c.status === 'Finalized';

  // Opens a print-ready page in a new tab; the office saves it as PDF from the browser.
  const print = () => {
    const q = new URLSearchParams(format ? { format } : {});
    window.open(apiUrl(`/api/report-cards/${c.id}/print?${q}`), '_blank');
  };

  const saveRemarks = async () => { setC(await reportCardsApi.setRemarks(c.id, remarks.trim() || null)); };
  const finalize = async () => {
    setBusy(true); setErr(null);
    try { setC(await reportCardsApi.finalize(c.id)); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Could not finalize.'); }
    finally { setBusy(false); }
  };
  const reopen = async () => { setC(await reportCardsApi.reopen(c.id)); };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 }} onClick={onClose}>
      <div className="card" style={{ padding: 22, maxWidth: 620, width: '100%', maxHeight: '90vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <h2 className="h-display" style={{ fontSize: 22, flex: 1 }}>{c.studentName}</h2>
          <button className="btn" onClick={onClose}>Close</button>
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>{c.term} · {c.academicYear} · {finalized ? 'Finalized' : 'Draft'}</div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 14 }}>
          <thead>
            <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--ink-3)' }}>
              <th style={{ padding: '6px 4px' }}>Subject</th><th>Marks</th><th>Max</th><th>Grade</th>
            </tr>
          </thead>
          <tbody>
            {c.lines.map((l) => (
              <tr key={l.subjectId + l.subjectName} style={{ borderTop: '1px solid var(--line)' }}>
                <td style={{ padding: '6px 4px' }}>{l.subjectName}</td>
                <td style={{ color: l.resolved ? 'inherit' : 'var(--brand,#c1272d)' }}>
                  {l.isAbsent ? 'Absent' : l.marks ?? 'Awaited'}
                </td>
                <td>{l.maxMarks}</td>
                <td>{l.grade ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {finalized && (
          <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 14 }}>
            <div><strong>Total:</strong> {c.totalObtained}/{c.totalMax}</div>
            <div><strong>Percentage:</strong> {c.percentage}%</div>
            {c.overallGrade && <div><strong>Grade:</strong> {c.overallGrade}</div>}
            {c.attendanceTotal != null && <div><strong>Attendance:</strong> {c.attendancePresent}/{c.attendanceTotal}</div>}
          </div>
        )}

        {!finalized && c.unresolved.length > 0 && (
          <div className="card" style={{ padding: 12, marginBottom: 14, background: '#fdecec', color: '#c1272d', fontSize: 13 }}>
            Missing marks for: {c.unresolved.join(', ')}. Enter these marks (or mark the student absent) before finalizing.
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label className="label">Remarks</label>
          <textarea className="input" rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={finalized} style={{ resize: 'vertical' }} />
          {!finalized && <button className="btn" style={{ marginTop: 6 }} onClick={saveRemarks}>Save remarks</button>}
        </div>

        {err && <div style={{ color: '#c1272d', fontSize: 13, marginBottom: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
          <select className="input" value={format} onChange={(e) => setFormat(e.target.value)} style={{ width: 'auto' }}>
            <option value="">Use this school's board</option>
            <option value="Cbse">CBSE layout (with co-scholastic)</option>
            <option value="StateBoard">State board / ICSE</option>
            <option value="PuCollege">PU college</option>
            <option value="University">University (credits · SGPA)</option>
          </select>
          <button className="btn" onClick={print}>Print / save as PDF</button>
          {!finalized && <span style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>A draft prints with a watermark.</span>}
        </div>

        {finalized
          ? <button className="btn" onClick={reopen}>Reopen for correction</button>
          : <button className="btn btn-accent" disabled={busy || c.unresolved.length > 0} onClick={finalize}>
              {busy ? 'Finalizing…' : c.unresolved.length > 0 ? 'Enter all marks to finalize' : 'Finalize card'}
            </button>}
      </div>
    </div>
  );
}
